import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderProps,
  BorderRadiusProps,
  BoxShadowProps,
  ColorProps,
  DisplayProps,
  HeightProps,
  SizeProps,
  WidthProps,
} from 'styled-system';
import {
  backgroundColor,
  border,
  borderRadius,
  boxShadow,
  color,
  display,
  height,
  size,
  space,
  themeGet,
  width,
} from 'styled-system';

import { Flex } from 'common/components/Flex';
import Icon, { Rotate } from 'common/components/Icon';
import type { ModifiersProps } from 'common/theme/helper';
import { modifiers } from 'common/theme/helper';
import type { FillProps, SpaceProps } from 'common/theme/system-types';
import type {
  BoxSizingProps,
  CursorProps,
  ObjectFitProps,
  ThemeMinHeightProps,
} from 'common/theme/system-utilities';
import { boxSizing, cursor, fill, objectFit, themeMinHeight } from 'common/theme/system-utilities';
import type { GraphqlNavigationItemProps } from 'common/types/NavigationItem';
import BaseNavigationLink from './BaseNavigationLink';
import { useNavigationItemProps } from './hooks/useNavigationItemProps';
import type { NavigationListItemProps } from './types';

const RIGHT_ARROW_SIZE = 16;

const UnstyledNavigationLine = styled(Flex)<ThemeMinHeightProps>`
  ${themeMinHeight};
  box-shadow: inset 0 -1px 0 ${themeGet('colors.sys.neutral.border.weak')};
`;

export const NavigationLine = (props: ComponentProps<typeof UnstyledNavigationLine>) => (
  <UnstyledNavigationLine alignItems="center" themeMinHeight="sp_48" {...props} />
);

const UnstyledNavigationItem = styled(NavigationLine)<BoxShadowProps>`
  &:last-of-type {
    box-shadow: none;
  }
  ${boxShadow};
`;

export const NavigationItem = (props: ComponentProps<typeof UnstyledNavigationItem>) => (
  <UnstyledNavigationItem
    alignItems="stretch"
    as="li"
    boxShadow="none"
    mx="sp_8"
    my="sp_2"
    {...props}
  />
);

const UnstyledButton = styled(BaseNavigationLink)<
  BackgroundColorProps &
    BorderRadiusProps &
    BorderProps &
    ColorProps &
    CursorProps &
    ModifiersProps &
    ThemeMinHeightProps & { isActive?: boolean }
>`
  ${backgroundColor};
  ${borderRadius};
  ${border};
  ${color};
  ${cursor};
  ${modifiers};
  ${themeMinHeight};

  &:hover {
    color: ${themeGet('colors.sys.neutral.text.active')};
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
    border: 1px solid ${themeGet('colors.sys.neutral.border.hover')};
    [role='presentation'] {
      fill: ${themeGet('colors.sys.neutral.icon.hover')};
    }
  }
  ${props =>
    props.isActive &&
    css`
      color: ${themeGet('colors.sys.neutral.text.active')(props)};
      background-color: ${themeGet('colors.sys.neutral.background.active')(props)};
      border: 1px solid ${themeGet('colors.sys.neutral.border.active')(props)};
      [role='presentation'] {
        fill: ${themeGet('colors.sys.neutral.icon.active')(props)};
      }
    }
  `};
`;

const Button = (props: ComponentProps<typeof UnstyledButton>) => (
  <UnstyledButton
    bg="transparent"
    border="1px solid transparent"
    borderRadius="medium"
    color="sys.neutral.text.default"
    cursor="pointer"
    m="sp_0"
    px="sp_8"
    themeMinHeight="sp_48"
    {...props}
  />
);

const UnstyledLink = styled(Button)<BoxSizingProps>`
  ${boxSizing};
`;

const Link = (props: ComponentProps<typeof UnstyledLink>) => (
  <UnstyledLink boxSizing="border-box" px="sp_8" tabIndex={0} themeMinHeight="sp_48" {...props} />
);

const UnstyledTopCategoryLink = styled(BaseNavigationLink)`
  text-underline-offset: ${themeGet('space.sp_4')}px;

  &:hover {
    background-color: ${themeGet('colors.sys.neutral.background.default')};
    font-weight: 700;
    text-decoration: solid underline ${themeGet('colors.sys.neutral.text.default')}
      ${themeGet('space.sp_2')}px;
  }

  &:active {
    color: ${themeGet('colors.sys.neutral.text.strong')};
    text-decoration: solid underline ${themeGet('colors.sys.neutral.text.strong')}
      ${themeGet('space.sp_2')}px;
  }
`;

const TopCategoryLink = (props: ComponentProps<typeof UnstyledTopCategoryLink>) => (
  <UnstyledTopCategoryLink textDecoration="underline" tabIndex={0} pl="sp_8" {...props} />
);

const RightArrow = styled(Icon)<WidthProps & SpaceProps & SizeProps & HeightProps & FillProps>`
  ${fill};
  ${height};
  ${size};
  ${space};
  ${width};
`;

const rightArrowStyles: ComponentProps<typeof RightArrow> = {
  name: 'arrowLargeBold',
  rotate: Rotate.Right,
  fill: 'sys.neutral.icon.default',
  pl: 'sp_8',
  size: RIGHT_ARROW_SIZE,
};

const Image = styled.img<
  DisplayProps & ObjectFitProps & SizeProps & SpaceProps & BorderRadiusProps
>`
  ${display};
  ${objectFit};
  ${size};
  ${space};
  ${borderRadius};
`;

const imageStyles: ComponentProps<typeof Image> = {
  borderRadius: 'small',
  display: 'inline-block',
  mr: 'sp_8',
  objectFit: 'contain',
  size: 32,
};

const isTopCategory = ({
  id,
  navLevel,
  selectedItem,
}: Pick<NavigationListItemProps, 'id' | 'navLevel' | 'selectedItem'>) =>
  id === 0 && (navLevel > 0 || selectedItem !== null);

const Thumbnail = ({
  thumbnail,
  name,
  isPrimary,
}: Pick<GraphqlNavigationItemProps, 'thumbnail' | 'isPrimary' | 'name'>) => (
  <Flex alignItems="center">
    {thumbnail?.url && isPrimary && <Image src={thumbnail.url} alt="" {...imageStyles} />}
    <span>{name}</span>
  </Flex>
);

export const NavigationItemBody = ({
  id,
  item,
  navLevel,
  selectedItem,
  isDefaultTab = true,
  ...props
}: NavigationListItemProps) => {
  const isAllCategory =
    isTopCategory({ id, navLevel, selectedItem }) ||
    (id === 0 && !isDefaultTab && !item.children?.length);
  const { navigationLinkProps, navigationButtonProps } = useNavigationItemProps({
    id,
    item,
    navLevel,
    selectedItem,
    ...props,
  });

  if (isAllCategory) {
    const { modifiers: _modifiers, ...topCategoryLinkProps } = navigationLinkProps;

    return <TopCategoryLink {...topCategoryLinkProps}>{item.name}</TopCategoryLink>;
  }

  return item.children?.length ? (
    <Button {...navigationButtonProps}>
      <Thumbnail {...item} />
      <RightArrow {...rightArrowStyles} />
    </Button>
  ) : (
    <Link {...navigationLinkProps}>
      <Thumbnail {...item} />
    </Link>
  );
};
